import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES } from '../../../i18n';
import { clearAiMemory, createConversation, createConversationNote, deleteConversationNote, DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE, findExistingDirectConversation, generateAiDraft, getAiMemory, getConversationMessagesPage, getConversationNotes, getInstagramChannelDefaults, moderateInstagramComment, moderateMessengerComment, refreshAiMemory, softDeleteMessage, toggleAiAssistant, toggleReaction, transcribeMessage, updateConversationNote, updateConversationPreferences } from '../services/conversationService';
import { useToast } from '../../notifications';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../../auth/context/AuthContext';
import { getMessageTypeLabel, mergeMessages } from '../utils/messageUtils';
import { mergeConversationSnapshotIntoList } from '../utils/conversationState';
import { appendAiThinkingStepHistory } from './ConversationsPage.aiThinkingHistory';
import { useConversationCreation } from '../hooks/useConversationCreation';
import { useConversationActions } from '../hooks/useConversationActions';
import { useConversationFiles } from '../hooks/useConversationFiles';
import { useConversationListState } from '../hooks/useConversationListState';
import { isAiAssistantDirectConversation, isDirectConversation as isDirectConversationType, useConversationPresentation } from '../hooks/useConversationPresentation';
import { useConversationNavigationData } from '../hooks/useConversationNavigationData';
import { useConversationMessagePane } from '../hooks/useConversationMessagePane';
import { useConversationRealtimeState } from '../hooks/useConversationRealtimeState';
import type { AiThinkingStepEvent } from '../hooks/useConversationRealtimeSubscription';
import { useConversationSearch } from '../hooks/useConversationSearch';
import { useMessageComposer } from '../hooks/useMessageComposer';
import { resolveConversationHasInstagramComments } from '../utils/instagramComposerUtils';
import { resolveConversationHasMessengerComments } from '../utils/messengerComposerUtils';
import {
  readShowCommentThreadsPreference,
  writeShowCommentThreadsPreference,
} from '../utils/conversationUiPreferences';
// ...existing code...
import { useConversationNotifications } from '../hooks/useConversationNotifications';
import { useSelectedConversation } from '../hooks/useSelectedConversation';
import { useContactChannelMasking } from '../../contacts/useContactChannelMasking';
import { Conversation, ConversationParticipantRef, Message } from '../types/Conversation';
import { ConversationListPane } from './ConversationListPane';
import ConversationNotesModal from './ConversationNotesModal';
import { ConversationsDetailsPane } from './ConversationsDetailsPane';
import { FilterChannel, FilterWhatsAppThread, NavSection, DraftsSentTab, parseNavSectionFromSearchParams, applyNavSectionToSearchParams, getCustomListIdFromNavSection, buildCustomListNavSection } from '../types/conversationNav';
import { useIsMobileLayout } from '../../../shared/hooks/useIsMobileLayout';
import pageStyles from './ConversationsPage.module.css';


const resolvePreferredLanguageCode = (fallbackLanguage?: string): string => {
  const normalize = (value?: string | null) => (value || '').trim().toLowerCase();

  const fromStorage = normalize(localStorage.getItem(LANGUAGE_STORAGE_KEY));
  if (SUPPORTED_LANGUAGES.includes(fromStorage as typeof SUPPORTED_LANGUAGES[number])) {
    return fromStorage;
  }

  const fromI18n = normalize(fallbackLanguage);
  if (SUPPORTED_LANGUAGES.includes(fromI18n as typeof SUPPORTED_LANGUAGES[number])) {
    return fromI18n;
  }

  return DEFAULT_LANGUAGE;
};

const renderExpandListIcon = (className: string) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4.5" y="5.5" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <rect x="5.5" y="6.5" width="4.5" height="11" rx="1" fill="currentColor" fillOpacity="0.18" />
    <path d="M10 5.5v13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M15.25 9.75 12.5 12l2.75 2.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const renderShowDetailsIcon = (className: string) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="4.5" y="5.5" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <rect x="10.5" y="6.5" width="8.5" height="11" rx="1" fill="currentColor" fillOpacity="0.18" />
    <path d="M10 5.5v13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M8.75 9.75 11.5 12 8.75 14.25" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const isSameParticipantRef = (left: { type?: string; id?: string }, right: { type?: string; id?: string }) => {
  return String(left.type || '').toLowerCase() === String(right.type || '').toLowerCase()
    && String(left.id || '').toLowerCase() === String(right.id || '').toLowerCase();
};

const applyOptimisticReaction = (
  conversation: Conversation,
  messageId: string,
  emoji: string,
  actor: Conversation['messages'][number]['sender'],
): Conversation => {
  let changed = false;

  const messages = conversation.messages.map(message => {
    if (message.id !== messageId) {
      return message;
    }

    const existing = message.reactions[emoji] || [];
    const alreadyReacted = existing.some(reactor => isSameParticipantRef(reactor, actor));
    const nextReactors = alreadyReacted
      ? existing.filter(reactor => !isSameParticipantRef(reactor, actor))
      : [...existing, actor];
    const nextReactions = { ...message.reactions };

    if (nextReactors.length > 0) {
      nextReactions[emoji] = nextReactors;
    } else {
      delete nextReactions[emoji];
    }

    changed = true;
    return {
      ...message,
      reactions: nextReactions,
    };
  });

  if (!changed) {
    return conversation;
  }

  return {
    ...conversation,
    messages,
  };
};

const ConversationsPage: React.FC = () => {
  const { t, i18n } = useTranslation(['conversations', 'common']);
  // Filter states
  const [filterStatus, setFilterStatus] = useState<'all' | 'signed' | 'unsigned'>('all');
  const [filterAiMuted, setFilterAiMuted] = useState<'all' | 'muted' | 'unmuted'>('all');
  const [filterChannel, setFilterChannel] = useState<FilterChannel>('all');
  const [filterWhatsAppThread, setFilterWhatsAppThread] = useState<FilterWhatsAppThread>('all');
  const [filterDate, setFilterDate] = useState<string>('');
  const [showCommentThreads, setShowCommentThreads] = useState(true);
  const showCommentThreadsHydratedRef = useRef(false);
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { setSelectedConversationId, setIsChatOpen, setIsChatMinimized } = useChat();
  const [navSection, setNavSection] = useState<NavSection>(() => parseNavSectionFromSearchParams(new URLSearchParams(window.location.search)));
  const [draftsSentTab, setDraftsSentTab] = useState<DraftsSentTab>('scheduled');
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const composerFileInputRef = useRef<HTMLInputElement | null>(null);
  const [isListExpanded, setIsListExpanded] = useState(false);
  const [isDetailsMaximized, setIsDetailsMaximized] = useState(false);
  const isMobileLayout = useIsMobileLayout();
  const maskChannelValues = useContactChannelMasking();
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null);
  const [pendingReactionKeys, setPendingReactionKeys] = useState<Set<string>>(new Set());
  const [openRowMenuId, setOpenRowMenuId] = useState<string | null>(null);
  const [expandScheduledMessagesSignal, setExpandScheduledMessagesSignal] = useState(0);
  const [transcribingMessageId, setTranscribingMessageId] = useState<string | null>(null);
  const [translatingMessageId, setTranslatingMessageId] = useState<string | null>(null);
  const [instagramDefaultCommentReplyMode, setInstagramDefaultCommentReplyMode] = useState<'public_comment' | 'private_comment'>('public_comment');
  const [messengerDefaultCommentReplyMode] = useState<'public_comment' | 'private_comment'>('public_comment');
  const [translatedMessagesById, setTranslatedMessagesById] = useState<Record<string, string>>({});
  const [translatedMessageVisibilityById, setTranslatedMessageVisibilityById] = useState<Record<string, boolean>>({});
  const [notesModalConversationId, setNotesModalConversationId] = useState<string | null>(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesBusy, setNotesBusy] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [aiHistoryStartSaving, setAiHistoryStartSaving] = useState(false);
  const [copyingAiSummary, setCopyingAiSummary] = useState(false);
  const [copyingAiMemory, setCopyingAiMemory] = useState(false);
  const [aiMemory, setAiMemory] = useState('');
  const [aiMemoryError, setAiMemoryError] = useState<string | null>(null);
  const [aiMemoryLoading, setAiMemoryLoading] = useState(false);
  const [refreshingAiMemory, setRefreshingAiMemory] = useState(false);
  const [clearingAiMemory, setClearingAiMemory] = useState(false);
  const [aiThinkingSteps, setAiThinkingSteps] = useState<AiThinkingStepEvent[]>([]);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const prefillAppliedNonceRef = useRef<number | null>(null);
  const interactiveReplyHandlerRef = useRef<((message: Message, replyText: string) => Promise<void>) | null>(null);
  const { auth } = useAuth();
  const currentEmployeeId = auth?.employeeId || undefined;

  useEffect(() => {
    if (!auth?.tenantId || (!auth?.employeeId && !auth?.username)) {
      showCommentThreadsHydratedRef.current = false;
      return;
    }

    setShowCommentThreads(readShowCommentThreadsPreference(auth.tenantId, auth.employeeId ?? undefined, auth.username));
    showCommentThreadsHydratedRef.current = true;
  }, [auth?.employeeId, auth?.tenantId, auth?.username]);

  useEffect(() => {
    if (!showCommentThreadsHydratedRef.current || !auth?.tenantId || (!auth?.employeeId && !auth?.username)) {
      return;
    }

    writeShowCommentThreadsPreference(
      showCommentThreads,
      auth.tenantId,
      auth.employeeId ?? undefined,
      auth.username,
    );
  }, [auth?.employeeId, auth?.tenantId, auth?.username, showCommentThreads]);

  const cachedConversationsRef = useRef<{
    list: Conversation[];
    hiddenList: Conversation[];
    customListConversations: Conversation[];
  }>({ list: [], hiddenList: [], customListConversations: [] });

  const resolveCachedConversation = useCallback((conversationId: string) => {
    const cached = cachedConversationsRef.current;
    return cached.list.find(conversation => conversation.id === conversationId)
      || cached.hiddenList.find(conversation => conversation.id === conversationId)
      || cached.customListConversations.find(conversation => conversation.id === conversationId);
  }, []);

  const {
    selectedId,
    setSelectedId,
    selectedConv,
    setSelectedConv,
    detailsLoading,
    detailsError,
    setDetailsError,
    meSender,
  } = useSelectedConversation({
    routeConversationId: id,
    navSection,
    employeeId: auth?.employeeId,
    tenantId: auth?.tenantId,
    resolveCachedConversation,
  });

  const onDetailsMaximizedChange = useCallback((maximized: boolean) => {
    setIsDetailsMaximized(maximized);
    if (maximized) {
      setIsListExpanded(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setIsDetailsMaximized(false);
    }
  }, [selectedId]);

  useEffect(() => {
    let cancelled = false;

    void getInstagramChannelDefaults()
      .then(defaults => {
        if (!cancelled) {
          setInstagramDefaultCommentReplyMode(defaults.defaultCommentReplyMode);
        }
      })
      .catch(() => {
        // Keep platform default when tenant setting is unavailable.
      });

    return () => {
      cancelled = true;
    };
  }, [auth?.tenantId]);

  const {
    messagesPaneRef,
    unreadCount,
    scrollToBottom,
  } = useConversationMessagePane({
    selectedId,
    selectedConv,
    detailsLoading,
    showCommentThreads,
  });

  const {
    list,
    hiddenList,
    customLists,
    customListConversations,
    loadingList,
    loadingMoreList,
    listHasNext,
    hiddenListHasNext,
    customListHasNext,
    listTotal,
    hiddenListTotal,
    listError,
    setList,
    setHiddenList,
    setCustomListConversations,
    loadConversationLists,
    loadMoreConversationList,
    createCustomList,
    updateCustomList,
    deleteCustomList,
    moveConversationToList,
    mergeCreatedConversationIntoList,
    clearSelectionForConversation,
    toggleFavoriteConversation,
    togglePinnedConversation,
    hideConversationForMe,
    restoreConversationForMe,
    deleteConversationForEveryone,
    employeeOptions,
    onAssignConversation,
    onUnassignConversation,
  } = useConversationListState({
    navSection,
    setNavSection,
    filterStatus,
    filterAiMuted,
    filterChannel,
    filterWhatsAppThread,
    filterDate,
    showCommentThreads,
    selectedId,
    navigate,
    setSelectedConversationId,
    setIsChatOpen,
    setSelectedConv,
    setDetailsError,
    setOpenRowMenuId,
    showToast,
  });

  cachedConversationsRef.current = { list, hiddenList, customListConversations };

  const applyConversationNotesState = useCallback((conversationId: string, patch: Pick<Conversation, 'note' | 'notes' | 'visibleNoteCount'>) => {
    setSelectedConv(current => current && current.id === conversationId ? { ...current, ...patch } : current);
    setList(current => current.map(conversation => conversation.id === conversationId ? { ...conversation, ...patch } : conversation));
    setHiddenList(current => current.map(conversation => conversation.id === conversationId ? { ...conversation, ...patch } : conversation));
  }, [setHiddenList, setList, setSelectedConv]);

  const closeConversationNotes = useCallback(() => {
    if (notesBusy) {
      return;
    }

    setNotesModalConversationId(null);
    setNotesLoading(false);
    setNotesError(null);
  }, [notesBusy]);

  const withNotesMutation = useCallback(async (action: () => Promise<void>) => {
    setNotesBusy(true);
    setNotesError(null);
    try {
      await action();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToUpdateNotes', { ns: 'conversations' });
      setNotesError(message);
      showToast(message, 'error');
    } finally {
      setNotesBusy(false);
    }
  }, [showToast, t]);

  const handleCreateConversationNote = useCallback(async (visibility: 'public' | 'private', text: string) => {
    const conversationId = notesModalConversationId ?? selectedConv?.id;
    if (!conversationId) {
      return;
    }

    await withNotesMutation(async () => {
      const updated = await createConversationNote(conversationId, { visibility, text });
      if (!updated) {
        throw new Error(t('conversationNotesCouldNotBeLoaded', { ns: 'conversations' }));
      }

      applyConversationNotesState(conversationId, {
        note: updated.note,
        notes: updated.notes,
        visibleNoteCount: updated.visibleNoteCount,
      });
      showToast(t('noteAdded', {
        ns: 'conversations',
        visibility: visibility === 'public' ? t('public', { ns: 'common' }) : t('private', { ns: 'common' }),
      }), 'success');
    });
  }, [applyConversationNotesState, notesModalConversationId, selectedConv?.id, showToast, withNotesMutation, t]);

  const handleUpdateConversationNote = useCallback(async (noteId: string, text: string) => {
    const conversationId = notesModalConversationId ?? selectedConv?.id;
    if (!conversationId) {
      return;
    }

    await withNotesMutation(async () => {
      const updated = await updateConversationNote(conversationId, noteId, { text });
      if (!updated) {
        throw new Error(t('conversationNotesCouldNotBeLoaded', { ns: 'conversations' }));
      }

      applyConversationNotesState(conversationId, {
        note: updated.note,
        notes: updated.notes,
        visibleNoteCount: updated.visibleNoteCount,
      });
      showToast(t('noteUpdated', { ns: 'conversations' }), 'success');
    });
  }, [applyConversationNotesState, notesModalConversationId, selectedConv?.id, showToast, withNotesMutation, t]);

  const handleDeleteConversationNote = useCallback(async (noteId: string) => {
    const conversationId = notesModalConversationId ?? selectedConv?.id;
    if (!conversationId) {
      return;
    }

    await withNotesMutation(async () => {
      const updated = await deleteConversationNote(conversationId, noteId);
      if (!updated) {
        throw new Error(t('conversationNotesCouldNotBeLoaded', { ns: 'conversations' }));
      }

      applyConversationNotesState(conversationId, {
        note: updated.note,
        notes: updated.notes,
        visibleNoteCount: updated.visibleNoteCount,
      });
      showToast(t('noteDeleted', { ns: 'conversations' }), 'success');
    });
  }, [applyConversationNotesState, notesModalConversationId, selectedConv?.id, showToast, withNotesMutation, t]);

  const isNotesModalOpen = Boolean(notesModalConversationId);

  const notesModalConversation = React.useMemo(() => {
    if (!notesModalConversationId) {
      return null;
    }

    if (selectedConv?.id === notesModalConversationId) {
      return selectedConv;
    }

    return list.find(conversation => conversation.id === notesModalConversationId)
      || hiddenList.find(conversation => conversation.id === notesModalConversationId)
      || customListConversations.find(conversation => conversation.id === notesModalConversationId)
      || {
        id: notesModalConversationId,
        participants: [],
        messages: [],
        notes: [],
        visibleNoteCount: 0,
        createdAt: '',
        updatedAt: '',
        isSigned: false,
        isAiAssistantMuted: false,
        isFavorite: false,
        isPinned: false,
      };
  }, [customListConversations, hiddenList, list, notesModalConversationId, selectedConv]);

  useEffect(() => {
    if (!notesModalConversationId || !isNotesModalOpen) {
      return;
    }

    let cancelled = false;
    setNotesLoading(true);
    setNotesError(null);

    void getConversationNotes(notesModalConversationId)
      .then(notesState => {
        if (cancelled || !notesState) {
          return;
        }

        applyConversationNotesState(notesModalConversationId, {
          note: notesState.note,
          notes: notesState.notes,
          visibleNoteCount: notesState.visibleNoteCount,
        });
      })
      .catch(notesLoadError => {
        if (cancelled) {
          return;
        }

        setNotesError(notesLoadError instanceof Error ? notesLoadError.message : t('conversationNotesCouldNotBeLoaded', { ns: 'conversations' }));
      })
      .finally(() => {
        if (!cancelled) {
          setNotesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applyConversationNotesState, isNotesModalOpen, notesModalConversationId, t]);

  useEffect(() => {
    if (!selectedConv) {
      return;
    }

    setList(current => mergeConversationSnapshotIntoList(current, selectedConv));
    setHiddenList(current => mergeConversationSnapshotIntoList(current, selectedConv));
  }, [selectedConv, setHiddenList, setList]);

  const loadOlderMessages = useCallback(async (targetMessageId?: string) => {
    if (!selectedId || !selectedConv || loadingOlderMessages) {
      return false;
    }

    if (targetMessageId && selectedConv.messages.some(message => message.id === targetMessageId)) {
      return true;
    }

    if (!selectedConv.hasMoreMessages) {
      return false;
    }

    setLoadingOlderMessages(true);

    try {
      let loadedCount = selectedConv.messages.length;
      let hasMoreMessages = Boolean(selectedConv.hasMoreMessages);
      let foundTarget = false;

      while (hasMoreMessages) {
        const pane = messagesPaneRef.current;
        const previousScrollHeight = pane?.scrollHeight ?? null;
        const previousScrollTop = pane?.scrollTop ?? null;
        const page = await getConversationMessagesPage(selectedId, {
          loadedCount,
          pageSize: DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE,
          includeSpam: navSection === 'spam' || Boolean(selectedConv.isSpam ?? selectedConv.isHidden),
        });

        if (!page) {
          return false;
        }

        if (targetMessageId && page.messages.some(message => message.id === targetMessageId)) {
          foundTarget = true;
        }

        loadedCount = page.loadedCount;
        hasMoreMessages = page.hasMoreMessages;

        setSelectedConv(current => {
          if (!current || current.id !== selectedId) {
            return current;
          }

          return {
            ...current,
            messages: mergeMessages(current.messages, page.messages),
            totalMessageCount: page.totalCount,
            hasMoreMessages: page.hasMoreMessages,
          };
        });

        if (pane && previousScrollHeight != null && previousScrollTop != null) {
          await new Promise<void>(resolve => {
            window.requestAnimationFrame(() => {
              pane.scrollTop = previousScrollTop + (pane.scrollHeight - previousScrollHeight);
              resolve();
            });
          });
        }

        if (!targetMessageId) {
          return page.messages.length > 0;
        }

        if (foundTarget) {
          return true;
        }

        if (page.messages.length === 0) {
          return false;
        }
      }

      return foundTarget;
    } catch {
      showToast(t('failedToLoadConversation', { ns: 'conversations' }), 'error');
      return false;
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [loadingOlderMessages, messagesPaneRef, navSection, selectedConv, selectedId, setSelectedConv, showToast, t]);

  const onSelect = useCallback((cid: string) => {
    setSelectedId(cid);
    const params = new URLSearchParams(window.location.search);
    applyNavSectionToSearchParams(params, navSection);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    navigate(`/conversations/${cid}${suffix}`);
    setIsListExpanded(false);
    setIsChatOpen(false);
  }, [navigate, navSection, setIsChatOpen, setSelectedId]);

  const onBackToList = useCallback(() => {
    setSelectedId(null);
    const params = new URLSearchParams(window.location.search);
    applyNavSectionToSearchParams(params, navSection);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    navigate(`/conversations${suffix}`);
    setIsListExpanded(true);
  }, [navigate, navSection, setSelectedId]);

  const openConversationNotes = useCallback((conversationId: string) => {
    setNotesError(null);
    setNotesModalConversationId(conversationId);
  }, []);

  const {
    creating,
    showCreatePanel,
    createConversationType,
    createTitle,
    createContactSearch,
    participantOptions,
    selectedParticipantId,
    selectedParticipantIds,
    loadingParticipantOptions,
    filteredParticipantOptions,
    setCreateTitle,
    setCreateContactSearch,
    openCreatePanel,
    onSetConversationType,
    onCancelCreatePanel,
    onCreate,
    toggleParticipantSelection,
    toggleChannelParticipantSelection,
    channelAccountsForCreate,
    loadingChannelAccountsForCreate,
    selectedChannelAccountId,
    setSelectedChannelAccountId,
    requiresChannelAccountSelection,
  } = useConversationCreation({
    navSection,
    list,
    locationKey: location.key,
    prefillContactId: (location.state as { prefillContactId?: string } | null)?.prefillContactId,
    employeeId: auth?.employeeId,
    tenantId: auth?.tenantId,
    isDirectConversation: isDirectConversationType,
    onSelectConversation: onSelect,
    onCreatedConversation: mergeCreatedConversationIntoList,
    showToast,
  });

  const onStartDirectMessageFromMention = useCallback(async (participant: {
    participantId: string;
    participantType: ConversationParticipantRef['type'];
    participantName: string;
  }) => {
    if (!participant.participantId || participant.participantType === 'system' || participant.participantType === 'businessSuite') {
      return;
    }

    if (!currentEmployeeId) {
      showToast(t('failedToCreateConversation', { ns: 'conversations' }), 'error');
      return;
    }

    const targetParticipant = { type: participant.participantType, id: participant.participantId };
    const existingConversation = await findExistingDirectConversation(targetParticipant.id);
    if (existingConversation) {
      const conversation = existingConversation.isAiAssistantMuted && isAiAssistantDirectConversation(existingConversation)
        ? await toggleAiAssistant(existingConversation.id, false) ?? existingConversation
        : existingConversation;
      mergeCreatedConversationIntoList(conversation);
      onSelect(conversation.id);
      showToast(t('openedExistingDirectMessage', { ns: 'conversations' }), 'info');
      return;
    }

    try {
      const createdConversation = await createConversation(undefined, [targetParticipant]);
      const conversation = createdConversation.isAiAssistantMuted && isAiAssistantDirectConversation(createdConversation)
        ? await toggleAiAssistant(createdConversation.id, false) ?? createdConversation
        : createdConversation;
      mergeCreatedConversationIntoList(conversation);
      onSelect(conversation.id);
      showToast(t('conversationCreatedSuccessfully', { ns: 'conversations' }), 'success');
    } catch {
      showToast(t('failedToCreateConversation', { ns: 'conversations' }), 'error');
    }
  }, [currentEmployeeId, mergeCreatedConversationIntoList, onSelect, showToast, t]);

  const handleTranslateMessage = useCallback(async (message: Message) => {
    if (!selectedConv || translatingMessageId) {
      return;
    }

    const originalText = message.content?.trim();
    if (!originalText) {
      return;
    }

    const targetLanguage = resolvePreferredLanguageCode(i18n.resolvedLanguage || i18n.language);
    const instruction = [
      'Translate the provided message into the requested language.',
      `Target language code: ${targetLanguage}`,
      'Return only the translated text. Do not include notes or explanations.',
      `Message: ${originalText}`,
    ].join('\n');

    setTranslatingMessageId(message.id);
    try {
      const translated = await generateAiDraft(selectedConv.id, instruction);
      const translatedText = translated?.content?.trim();
      if (!translatedText) {
        showToast(t('failedToTranslateMessage', { ns: 'conversations' }), 'error');
        return;
      }

      setTranslatedMessagesById(current => ({
        ...current,
        [message.id]: translatedText,
      }));
      setTranslatedMessageVisibilityById(current => ({
        ...current,
        [message.id]: true,
      }));
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToTranslateMessage', { ns: 'conversations' }), 'error');
    } finally {
      setTranslatingMessageId(current => current === message.id ? null : current);
    }
  }, [i18n.language, i18n.resolvedLanguage, selectedConv, showToast, t, translatingMessageId]);

  const handleToggleTranslatedMessageVisibility = useCallback((messageId: string) => {
    setTranslatedMessageVisibilityById(current => ({
      ...current,
      [messageId]: !(current[messageId] ?? true),
    }));
  }, []);

  const handleReplyToInteractiveSelection = useCallback((message: Message, replyText: string) => {
    return interactiveReplyHandlerRef.current?.(message, replyText);
  }, []);

  const {
    currentConversationList,
    getParticipantDisplayName,
    getConversationDisplayName,
    renderMessageContent,
    mentionDetailsPopup,
    imageViewerPopup,
  } = useConversationPresentation({
    list,
    hiddenList,
    customListConversations,
    navSection,
    participantOptions,
    enableInteractiveSelection: true,
    currentViewerParticipant: meSender,
    currentEmployeeId,
    transcribingMessageId,
    translatingMessageId,
    translatedMessagesById,
    translatedMessageVisibilityById,
    onReplyToInteractiveSelection: handleReplyToInteractiveSelection,
    maskChannelValues,
    onStartDirectMessage: onStartDirectMessageFromMention,
    onTranscribeAudio: async (message) => {
      if (!selectedConv || transcribingMessageId) {
        return;
      }

      setTranscribingMessageId(message.id);
      try {
        const updatedMessage = await transcribeMessage(selectedConv.id, message.id);
        if (!updatedMessage) {
          showToast(t('audioMessageNotFound', { ns: 'conversations' }), 'error');
          return;
        }

        setSelectedConv(current => {
          if (!current || current.id !== selectedConv.id) {
            return current;
          }

          return {
            ...current,
            messages: current.messages.map(existing => existing.id === updatedMessage.id ? updatedMessage : existing),
          };
        });

        const updateConversationMessage = (conversation: Conversation): Conversation => {
          if (conversation.id !== selectedConv.id) {
            return conversation;
          }

          return {
            ...conversation,
            messages: conversation.messages.map(existing => existing.id === updatedMessage.id ? updatedMessage : existing),
          };
        };

        setList(current => current.map(updateConversationMessage));
        setHiddenList(current => current.map(updateConversationMessage));
      } catch (error) {
        showToast(error instanceof Error ? error.message : t('failedToTranscribeAudio', { ns: 'conversations' }), 'error');
      } finally {
        setTranscribingMessageId(current => current === message.id ? null : current);
      }
    },
    onTranslateMessage: handleTranslateMessage,
    onToggleTranslatedMessageVisibility: handleToggleTranslatedMessageVisibility,
  });

  const {
    showFilesPanel,
    setShowFilesPanel,
    filesSearchTerm,
    setFilesSearchTerm,
    filesPanelRef,
    conversationFiles,
    groupedConversationFiles,
  } = useConversationFiles({
    selectedConv,
    getParticipantDisplayName,
    getMessageTypeLabel,
  });

  useEffect(() => {
    const section = parseNavSectionFromSearchParams(new URLSearchParams(location.search));
    setNavSection(section);
  }, [location.search]);

  const onSetNavSection = useCallback((nextSection: NavSection) => {
    setNavSection(nextSection);

    const params = new URLSearchParams(location.search);
    applyNavSectionToSearchParams(params, nextSection);

    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const handleCreateCustomList = useCallback(async () => {
    const name = window.prompt(t('createCustomListPrompt', { ns: 'conversations' }));
    if (!name?.trim()) {
      return;
    }

    const created = await createCustomList(name.trim());
    if (created) {
      onSetNavSection(buildCustomListNavSection(created.id));
    }
  }, [createCustomList, onSetNavSection, t]);

  const openConversationFromNotification = useCallback((conversationId: string) => {
    window.focus();
    onSetNavSection('conversations');
    onSelect(conversationId);
  }, [onSelect, onSetNavSection]);

  const {
    notificationPermission,
    isSoundEnabled,
    unreadCountByConversationId,
    playNotificationSound,
    onToggleSound,
    handleConversationNotification,
    applyListItemUnreadCounts,
  } = useConversationNotifications({
    list,
    hiddenList,
    selectedConversationId: selectedConv?.id,
    selectedConversation: selectedConv,
    showCommentThreads,
    employeeId: auth?.employeeId,
    tenantId: auth?.tenantId,
    onOpenConversationFromNotification: openConversationFromNotification,
  });

  const {
    search,
    setSearch,
    showSearchPanel,
    setShowSearchPanel,
    searchInputRef,
    globalSearchResults,
    orderedFilteredList,
    onSelectSearchResult,
  } = useConversationSearch({
    currentConversationList,
    navSection,
    filterStatus,
    filterAiMuted,
    filterChannel,
    filterWhatsAppThread,
    filterDate,
    showCommentThreads,
    getConversationDisplayName,
    onSelectConversation: onSelect,
  });

  const activeCustomListId = getCustomListIdFromNavSection(navSection);
  const hasMoreConversations = navSection === 'spam'
    ? hiddenListHasNext
    : activeCustomListId
      ? customListHasNext
      : listHasNext;

  React.useEffect(() => {
    if (loadingList || loadingMoreList || !hasMoreConversations) {
      return;
    }

    if (navSection === 'drafts-sent') {
      return;
    }

    if (search.trim().length >= 2) {
      return;
    }

    const minimumVisibleRows = 8;
    if (orderedFilteredList.length >= minimumVisibleRows) {
      return;
    }

    void loadMoreConversationList();
  }, [
    hasMoreConversations,
    loadMoreConversationList,
    loadingList,
    loadingMoreList,
    navSection,
    orderedFilteredList.length,
    search,
  ]);

  const onOpenFloatingChat = useCallback((conversationId: string) => {
    setSelectedConversationId(conversationId);
    setIsChatOpen(true);
    setIsChatMinimized(false);
  }, [setIsChatMinimized, setIsChatOpen, setSelectedConversationId]);

  const {
    signing,
    toggleAiAssistantBusy,
    onSign,
    onToggleAiAssistant,
  } = useConversationActions({
    selectedConv,
    setSelectedConv,
    setList,
    showToast,
  });

  useEffect(() => {
    setAiThinkingSteps([]);
  }, [selectedConv?.id]);

  const appendAiThinkingStep = useCallback((event: AiThinkingStepEvent) => {
    setAiThinkingSteps(current => appendAiThinkingStepHistory(current, event));
  }, []);

  const clearAiThinkingSteps = useCallback(() => {
    setAiThinkingSteps([]);
  }, []);

  const handleUpdateAiHistoryStartAtUtc = useCallback(async (value: string | null) => {
    if (!selectedConv) {
      return;
    }

    setAiHistoryStartSaving(true);
    try {
      const updated = await updateConversationPreferences(selectedConv.id, { aiHistoryStartAtUtc: value });
      if (!updated) {
        throw new Error(t('failedToUpdateAiHistoryStartAtUtc', { ns: 'conversations' }));
      }

      // Only the cutoff changed, so keep the loaded message window and its pagination state
      // instead of adopting the response wholesale.
      const nextSelectedConv: Conversation = {
        ...selectedConv,
        aiHistoryStartAtUtc: updated.aiHistoryStartAtUtc,
        updatedAt: updated.updatedAt,
      };

      setSelectedConv(nextSelectedConv);
      setList(current => mergeConversationSnapshotIntoList(current, nextSelectedConv));
      setHiddenList(current => mergeConversationSnapshotIntoList(current, nextSelectedConv));
      showToast(
        value
          ? t('aiHistoryStartAtUtcSaved', { ns: 'conversations' })
          : t('aiHistoryStartAtUtcCleared', { ns: 'conversations' }),
        'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToUpdateAiHistoryStartAtUtc', { ns: 'conversations' }), 'error');
    } finally {
      setAiHistoryStartSaving(false);
    }
  }, [selectedConv, setHiddenList, setList, setSelectedConv, showToast, t]);

  const { onMessagesCommitted, onMessagesReconciled, onMessagesRemoved } = useConversationRealtimeState({
    tenantId: auth?.tenantId,
    navSection,
    selectedId,
    selectedConv,
    setSelectedConv,
    setList,
    setHiddenList,
    setCustomListConversations,
    loadConversationLists,
    clearSelectionForConversation,
    handleConversationNotification,
    onListItemUnreadCounts: applyListItemUnreadCounts,
    onAiThinkingStep: appendAiThinkingStep,
    onAiThinkingCompleted: clearAiThinkingSteps,
    showToast,
  });

  const {
    composer,
    setComposer,
    quotedMessage,
    mentionSuggestions,
    mentionMenuOpen,
    mentionActiveIndex,
    commandSuggestions,
    commandMenuOpen,
    commandActiveIndex,
    interactiveComposerMode,
    setInteractiveComposerMode,
    interactiveListDraft,
    setInteractiveListDraft,
    interactiveButtonsDraft,
    setInteractiveButtonsDraft,
    isInteractiveComposerOpen,
    setIsInteractiveComposerOpen,
    hasInteractiveListDraft,
    hasInteractiveButtonsDraft,
    hasInteractiveDraft,
    attachments,
    isUploaderExpanded,
    setIsUploaderExpanded,
    isRecordingAudio,
    isShortcutMenuOpen,
    setIsShortcutMenuOpen,
    dragActive,
    sendError,
    sendingMessage,
    sendingProgressLabel,
    aiDraft,
    aiDraftError,
    aiDraftLoading,
    aiSummary,
    aiSummaryError,
    aiSummaryLoading,
    scheduledAt,
    setScheduledAt,
    scheduling,
    scheduleError,
    scheduledMessages,
    onSchedule,
    onScheduleAt,
    onCancelScheduledMessage,
    onUpdateScheduledMessageTime,
    onSend,
    onGenerateAiDraft,
    onGenerateAiSummary,
    onAudioRecordToggle,
    onShortcutSelectCommand,
    insertComposerEmoji,
    removeAttachment,
    onPickAttachments,
    onComposerDragOver,
    onComposerDragLeave,
    onComposerDrop,
    onComposerChange,
    onComposerSelectionChange,
    onComposerFocus,
    onComposerKeyDown,
    onSelectMention,
    onSelectCommand,
    onQuoteMessage,
    onSelectInstagramCommentTarget,
    onClearInstagramCommentTarget,
    onSelectMessengerCommentTarget,
    onClearMessengerCommentTarget,
    onReplyWithInteractiveSelection,
    onClearQuotedMessage,
    onUseAiDraft,
    onUseAiSummary,
    onDismissAiDraft,
    onDismissAiSummary,
    showWhatsAppLinkPreviewToggle,
    whatsAppLinkPreviewEnabled,
    setWhatsAppLinkPreviewEnabled,
    instagramComposerContext,
    instagramReplyMode,
    setInstagramReplyMode,
    instagramCommentTargetMessageId,
    setInstagramCommentTargetMessageId,
    messengerComposerContext,
    messengerReplyMode,
    setMessengerReplyMode,
    messengerCommentTargetMessageId,
    setMessengerCommentTargetMessageId,
  } = useMessageComposer({
    conversationId: selectedConv?.id,
    meSender,
    containsWhatsAppMessages: Boolean(selectedConv?.containsWhatsAppMessages),
    containsInstagramMessages: Boolean(selectedConv?.containsInstagramMessages ?? selectedConv?.messages?.some(message => message.source === 'instagram')),
    containsInstagramComments: Boolean(selectedConv && resolveConversationHasInstagramComments(selectedConv)),
    containsMessengerMessages: Boolean(selectedConv?.messages?.some(message => message.source === 'messenger')),
    containsMessengerComments: Boolean(selectedConv && resolveConversationHasMessengerComments(selectedConv)),
    containsWebchatMessages: Boolean(
      selectedConv?.participants?.some(participant => participant.contactChannelLabel?.trim().toLowerCase() === 'webchat'
        || participant.contactChannelValue?.trim().toLowerCase().startsWith('webchat:'))
      || selectedConv?.messages?.some(message => message.source === 'webchat'),
    ),
    conversationMessages: selectedConv?.messages ?? [],
    hasExternalParticipant: Boolean(selectedConv?.hasExternalParticipant),
    instagramDefaultCommentReplyMode,
    messengerDefaultCommentReplyMode,
    fileInputRef: composerFileInputRef,
    composerInputRef,
    resetKey: selectedConv?.id,
    onMessagesCommitted,
    onMessagesReconciled,
    onMessagesRemoved,
    onAiDraftFailure: () => showToast(t('failedToGenerateAiDraft', { ns: 'conversations' }), 'error'),
  });

  interactiveReplyHandlerRef.current = onReplyWithInteractiveSelection;

  const onGenerateAiDraftWithThinking = useCallback(() => {
    setAiThinkingSteps([]);
    onGenerateAiDraft();
  }, [onGenerateAiDraft]);

  const onGenerateAiSummaryWithThinking = useCallback(() => {
    setAiThinkingSteps([]);
    onGenerateAiSummary();
  }, [onGenerateAiSummary]);

  useEffect(() => {
    const state = (location.state as {
      prefillComposerText?: string;
      prefillComposerNonce?: number;
      prefillComposerSource?: string;
    } | null) ?? null;

    if (!state?.prefillComposerText?.trim() || !selectedConv?.id) {
      return;
    }

    const nonce = Number(state.prefillComposerNonce ?? 0);
    if (nonce > 0 && prefillAppliedNonceRef.current === nonce) {
      return;
    }

    if (nonce > 0) {
      prefillAppliedNonceRef.current = nonce;
    }

    const snippet = state.prefillComposerText.trim();
    setComposer(previous => (previous.trim() ? `${previous.trim()}\n\n${snippet}` : snippet));

    const nextState = { ...state } as Record<string, unknown>;
    delete nextState.prefillComposerText;
    delete nextState.prefillComposerNonce;
    delete nextState.prefillComposerSource;

    navigate(
      {
        pathname: location.pathname,
        search: location.search,
      },
      {
        replace: true,
        state: Object.keys(nextState).length > 0 ? nextState : null,
      },
    );
  }, [location.pathname, location.search, location.state, navigate, selectedConv?.id, setComposer]);

  const handleSaveAiSummaryAsNote = useCallback(async () => {
    if (!selectedConv || !aiSummary.trim()) {
      return;
    }

    await withNotesMutation(async () => {
      const updated = await createConversationNote(selectedConv.id, { visibility: 'public', text: aiSummary.trim() });
      if (!updated) {
        throw new Error(t('conversationNotesCouldNotBeLoaded', { ns: 'conversations' }));
      }

      applyConversationNotesState(selectedConv.id, {
        note: updated.note,
        notes: updated.notes,
        visibleNoteCount: updated.visibleNoteCount,
      });
      onDismissAiSummary();
      showToast(t('summarySavedAsNote', { ns: 'conversations' }), 'success');
    });
  }, [aiSummary, applyConversationNotesState, onDismissAiSummary, selectedConv, showToast, t, withNotesMutation]);

  const handleCopyAiSummary = useCallback(async () => {
    if (!aiSummary.trim() || copyingAiSummary) {
      return;
    }

    if (!navigator.clipboard?.writeText) {
      showToast(t('copySummaryNotSupported', { ns: 'conversations' }), 'error');
      return;
    }

    setCopyingAiSummary(true);
    try {
      await navigator.clipboard.writeText(aiSummary.trim());
      showToast(t('summaryCopied', { ns: 'conversations' }), 'success');
    } catch {
      showToast(t('failedToCopySummary', { ns: 'conversations' }), 'error');
    } finally {
      setCopyingAiSummary(false);
    }
  }, [aiSummary, copyingAiSummary, showToast, t]);

  const handleCopyAiMemory = useCallback(async () => {
    if (!aiMemory.trim() || copyingAiMemory) {
      return;
    }

    if (!navigator.clipboard?.writeText) {
      showToast(t('copyAiMemoryNotSupported', { ns: 'conversations', defaultValue: 'Copying AI memory is not supported in this browser.' }), 'error');
      return;
    }

    setCopyingAiMemory(true);
    try {
      await navigator.clipboard.writeText(aiMemory.trim());
      showToast(t('aiMemoryCopied', { ns: 'conversations', defaultValue: 'AI memory copied.' }), 'success');
    } catch {
      showToast(t('failedToCopyAiMemory', { ns: 'conversations', defaultValue: 'Failed to copy AI memory.' }), 'error');
    } finally {
      setCopyingAiMemory(false);
    }
  }, [aiMemory, copyingAiMemory, showToast, t]);

  useEffect(() => {
    const conversationId = selectedConv?.id;
    if (!conversationId) {
      setAiMemory('');
      setAiMemoryError(null);
      setAiMemoryLoading(false);
      return;
    }

    let cancelled = false;
    setAiMemoryLoading(true);
    setAiMemoryError(null);

    void getAiMemory(conversationId)
      .then(memoryState => {
        if (cancelled) {
          return;
        }

        setAiMemory(memoryState?.content ?? '');
      })
      .catch(error => {
        if (cancelled) {
          return;
        }

        setAiMemory('');
        setAiMemoryError(error instanceof Error ? error.message : t('failedToLoadAiMemory', { ns: 'conversations', defaultValue: 'Failed to load AI memory.' }));
      })
      .finally(() => {
        if (!cancelled) {
          setAiMemoryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedConv?.id, t]);

  const handleRefreshAiMemory = useCallback(async () => {
    if (!selectedConv || refreshingAiMemory || clearingAiMemory) {
      return;
    }

    setRefreshingAiMemory(true);
    setAiMemoryError(null);
    try {
      const memoryState = await refreshAiMemory(selectedConv.id);
      if (!memoryState?.content) {
        throw new Error(t('failedToRefreshAiMemory', { ns: 'conversations', defaultValue: 'AI could not refresh memory for this conversation.' }));
      }

      setAiMemory(memoryState.content);
      showToast(t('aiMemoryRefreshed', { ns: 'conversations', defaultValue: 'AI memory refreshed.' }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToRefreshAiMemory', { ns: 'conversations', defaultValue: 'AI could not refresh memory for this conversation.' });
      setAiMemoryError(message);
      showToast(message, 'error');
    } finally {
      setRefreshingAiMemory(false);
    }
  }, [clearingAiMemory, refreshingAiMemory, selectedConv, showToast, t]);

  const handleClearAiMemory = useCallback(async () => {
    if (!selectedConv || clearingAiMemory || refreshingAiMemory) {
      return;
    }

    setClearingAiMemory(true);
    setAiMemoryError(null);
    try {
      await clearAiMemory(selectedConv.id);
      setAiMemory('');
      showToast(t('aiMemoryCleared', { ns: 'conversations', defaultValue: 'AI memory cleared.' }), 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToClearAiMemory', { ns: 'conversations', defaultValue: 'Failed to clear AI memory.' });
      setAiMemoryError(message);
      showToast(message, 'error');
    } finally {
      setClearingAiMemory(false);
    }
  }, [clearingAiMemory, refreshingAiMemory, selectedConv, showToast, t]);

  // Copilot/AI insights logic removed
  // Copilot/AI insights logic removed

  // Copilot/AI insights logic removed

  const {
    loadingScheduledNav,
    scheduledNavItems,
    sentNavItems,
    directConversationCount,
    channelConversationCount,
    externalConversationCount,
    onSelectScheduledNavItem,
    onSelectSentNavItem,
    fetchScheduledNav,
  } = useConversationNavigationData({
    list,
    hiddenList,
    getConversationDisplayName,
    onSelectConversation: onSelect,
    onExpandScheduledMessages: () => setExpandScheduledMessagesSignal(current => current + 1),
  });

  useEffect(() => {
    if (navSection === 'drafts-sent' && draftsSentTab === 'scheduled') {
      fetchScheduledNav();
    }
  }, [draftsSentTab, fetchScheduledNav, navSection, scheduledMessages]);

  const onReaction = async (messageId: string, emoji: string) => {
    if (!selectedConv || !meSender) return;

    const conversationId = selectedConv.id;
    const pendingKey = `${conversationId}:${messageId}:${emoji}`;
    const snapshotBeforeToggle = selectedConv;
    const optimistic = applyOptimisticReaction(snapshotBeforeToggle, messageId, emoji, meSender);

    setPendingReactionKeys(current => {
      const next = new Set(current);
      next.add(pendingKey);
      return next;
    });
    setSelectedConv(optimistic);
    setList(current => mergeConversationSnapshotIntoList(current, optimistic));
    setHiddenList(current => mergeConversationSnapshotIntoList(current, optimistic));
    setShowReactionPicker(null);

    try {
      const updated = await toggleReaction(conversationId, messageId, emoji, meSender);
      if (updated) {
        setSelectedConv(current => (current && current.id === conversationId ? updated : current));
        setList(current => mergeConversationSnapshotIntoList(current, updated));
        setHiddenList(current => mergeConversationSnapshotIntoList(current, updated));
      }
    } catch {
      setSelectedConv(current => (current && current.id === conversationId ? snapshotBeforeToggle : current));
      setList(current => mergeConversationSnapshotIntoList(current, snapshotBeforeToggle));
      setHiddenList(current => mergeConversationSnapshotIntoList(current, snapshotBeforeToggle));
      showToast(t('failedToLoadConversation', { ns: 'conversations' }), 'error');
    } finally {
      setPendingReactionKeys(current => {
        const next = new Set(current);
        next.delete(pendingKey);
        return next;
      });
    }
  };

  const onModerateInstagramComment = async (messageId: string, action: 'hide' | 'unhide' | 'delete') => {
    if (!selectedConv) return;

    const conversationId = selectedConv.id;
    const snapshotBeforeModeration = selectedConv;

    try {
      const updatedMessage = await moderateInstagramComment(conversationId, messageId, action);
      if (!updatedMessage) {
        showToast(t('instagramCommentModerationFailed', { ns: 'conversations' }), 'error');
        return;
      }

      const patchConversation = (conversation: Conversation): Conversation => ({
        ...conversation,
        messages: conversation.messages.map(message => (
          message.id === messageId ? updatedMessage : message
        )),
      });

      setSelectedConv(current => (current && current.id === conversationId ? patchConversation(current) : current));
      setList(current => current.map(conversation => (
        conversation.id === conversationId ? patchConversation(conversation) : conversation
      )));
      setHiddenList(current => current.map(conversation => (
        conversation.id === conversationId ? patchConversation(conversation) : conversation
      )));
    } catch {
      setSelectedConv(current => (current && current.id === conversationId ? snapshotBeforeModeration : current));
      showToast(t('instagramCommentModerationFailed', { ns: 'conversations' }), 'error');
    }
  };

  const onModerateMessengerComment = async (messageId: string, action: 'hide' | 'unhide' | 'delete') => {
    if (!selectedConv) return;

    const conversationId = selectedConv.id;
    const snapshotBeforeModeration = selectedConv;

    try {
      const updatedMessage = await moderateMessengerComment(conversationId, messageId, action);
      if (!updatedMessage) {
        showToast(t('messengerCommentModerationFailed', { ns: 'conversations' }), 'error');
        return;
      }

      const patchConversation = (conversation: Conversation): Conversation => ({
        ...conversation,
        messages: conversation.messages.map(message => (
          message.id === messageId ? updatedMessage : message
        )),
      });

      setSelectedConv(current => (current && current.id === conversationId ? patchConversation(current) : current));
      setList(current => current.map(conversation => (
        conversation.id === conversationId ? patchConversation(conversation) : conversation
      )));
      setHiddenList(current => current.map(conversation => (
        conversation.id === conversationId ? patchConversation(conversation) : conversation
      )));
    } catch {
      setSelectedConv(current => (current && current.id === conversationId ? snapshotBeforeModeration : current));
      showToast(t('messengerCommentModerationFailed', { ns: 'conversations' }), 'error');
    }
  };

  const onDeleteMessage = async (messageId: string) => {
    if (!selectedConv) return;

    const conversationId = selectedConv.id;
    const snapshotBeforeDelete = selectedConv;

    const removeMessage = (conversation: Conversation): Conversation => ({
      ...conversation,
      messages: conversation.messages.filter(message => message.id !== messageId),
    });

    setSelectedConv(current => (current && current.id === conversationId ? removeMessage(current) : current));
    setList(current => current.map(conversation => (
      conversation.id === conversationId ? removeMessage(conversation) : conversation
    )));
    setHiddenList(current => current.map(conversation => (
      conversation.id === conversationId ? removeMessage(conversation) : conversation
    )));

    try {
      await softDeleteMessage(conversationId, messageId);
    } catch {
      setSelectedConv(current => (current && current.id === conversationId ? snapshotBeforeDelete : current));
      showToast(t('failedToDeleteMessage', { ns: 'conversations' }), 'error');
    }
  };

  useEffect(() => {
    if (unreadCount > 0 && isSoundEnabled) {
      void playNotificationSound();
    }
  }, [unreadCount, isSoundEnabled, playNotificationSound]);

  return (
    <div className={pageStyles.pageRoot}>
    <div className={`${pageStyles.layout} ${isMobileLayout ? pageStyles.layoutMobile : ''} ${isDetailsMaximized ? pageStyles.layoutDetailsMaximized : ''}`}>
      <ConversationListPane
        isListExpanded={isListExpanded}
        isDetailsMaximized={isDetailsMaximized}
        isMobileLayout={isMobileLayout}
        navSection={navSection}
        setNavSection={onSetNavSection}
        conversationCount={listTotal}
        directConversationCount={directConversationCount}
        channelConversationCount={channelConversationCount}
        externalConversationCount={externalConversationCount}
        hiddenConversationCount={hiddenListTotal}
        draftsSentCount={scheduledNavItems.length + sentNavItems.length}
        draftsSentTab={draftsSentTab}
        setDraftsSentTab={(tab) => {
          setDraftsSentTab(tab);
          if (tab === 'scheduled') fetchScheduledNav();
        }}
        creating={creating}
        openCreatePanel={openCreatePanel}
        showCreatePanel={showCreatePanel}
        createConversationType={createConversationType}
        onSetConversationType={onSetConversationType}
        createTitle={createTitle}
        setCreateTitle={setCreateTitle}
        createContactSearch={createContactSearch}
        setCreateContactSearch={setCreateContactSearch}
        loadingParticipantOptions={loadingParticipantOptions}
        filteredParticipantOptions={filteredParticipantOptions}
        selectedParticipantId={selectedParticipantId}
        selectedParticipantIds={selectedParticipantIds}
        toggleParticipantSelection={toggleParticipantSelection}
        toggleChannelParticipantSelection={toggleChannelParticipantSelection}
        participantOptions={participantOptions}
        onCancelCreatePanel={onCancelCreatePanel}
        onCreate={onCreate}
        channelAccountsForCreate={channelAccountsForCreate}
        loadingChannelAccountsForCreate={loadingChannelAccountsForCreate}
        selectedChannelAccountId={selectedChannelAccountId}
        setSelectedChannelAccountId={setSelectedChannelAccountId}
        requiresChannelAccountSelection={requiresChannelAccountSelection}
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        filterAiMuted={filterAiMuted}
        setFilterAiMuted={setFilterAiMuted}
        filterChannel={filterChannel}
        setFilterChannel={setFilterChannel}
        filterWhatsAppThread={filterWhatsAppThread}
        setFilterWhatsAppThread={setFilterWhatsAppThread}
        filterDate={filterDate}
        setFilterDate={setFilterDate}
        showCommentThreads={showCommentThreads}
        setShowCommentThreads={setShowCommentThreads}
        searchInputRef={searchInputRef}
        search={search}
        setSearch={setSearch}
        globalSearchResults={globalSearchResults}
        showSearchPanel={showSearchPanel}
        setShowSearchPanel={setShowSearchPanel}
        onSelectSearchResult={onSelectSearchResult}
        loadingList={loadingList}
        loadingMoreList={loadingMoreList}
        hasMoreConversations={hasMoreConversations}
        onLoadMoreConversations={loadMoreConversationList}
        loadingScheduledNav={loadingScheduledNav}
        listError={listError}
        orderedFilteredList={orderedFilteredList}
        unreadCountByConversationId={unreadCountByConversationId}
        scheduledNavItems={scheduledNavItems}
        sentNavItems={sentNavItems}
        selectedId={selectedId}
        onSelectConversation={onSelect}
        onSelectScheduledNavItem={onSelectScheduledNavItem}
        onSelectSentNavItem={onSelectSentNavItem}
        getConversationDisplayName={getConversationDisplayName}
        currentEmployeeId={currentEmployeeId}
        onOpenFloatingChat={onOpenFloatingChat}
        openRowMenuId={openRowMenuId}
        setOpenRowMenuId={setOpenRowMenuId}
        togglePinnedConversation={togglePinnedConversation}
        toggleFavoriteConversation={toggleFavoriteConversation}
        hideConversationForMe={hideConversationForMe}
        restoreConversationForMe={restoreConversationForMe}
        deleteConversationForEveryone={deleteConversationForEveryone}
        onOpenConversationNotes={openConversationNotes}
        employeeOptions={employeeOptions}
        onAssignConversation={onAssignConversation}
        onUnassignConversation={onUnassignConversation}
        customLists={customLists}
        onCreateCustomList={handleCreateCustomList}
        onUpdateCustomList={updateCustomList}
        onDeleteCustomList={deleteCustomList}
        onMoveConversationToCustomList={moveConversationToList}
        maskChannelValues={maskChannelValues}
      />

      {!isDetailsMaximized && (
      <button 
        className={pageStyles.toggleBtn}
        onClick={() => {
          setIsListExpanded(current => {
            const next = !current;
            if (next) {
              setIsDetailsMaximized(false);
            }

            return next;
          });
        }}
        title={isListExpanded ? t('showDetails', { ns: 'conversations' }) : t('expandList', { ns: 'conversations' })}
        aria-label={isListExpanded ? t('showDetails', { ns: 'conversations' }) : t('expandList', { ns: 'conversations' })}
      >
        {isListExpanded ? renderShowDetailsIcon(pageStyles.toggleIcon) : renderExpandListIcon(pageStyles.toggleIcon)}
      </button>
      )}

      <ConversationsDetailsPane
        isListExpanded={isListExpanded}
        isDetailsMaximized={isDetailsMaximized}
        onDetailsMaximizedChange={onDetailsMaximizedChange}
        isMobileLayout={isMobileLayout}
        onBackToList={onBackToList}
        expandScheduledMessagesSignal={expandScheduledMessagesSignal}
        selectedId={selectedId}
        detailsLoading={detailsLoading}
        detailsError={detailsError}
        selectedConv={selectedConv}
        showCommentThreads={showCommentThreads}
        signing={signing}
        toggleAiAssistantBusy={toggleAiAssistantBusy}
        onSign={onSign}
        onDeleteConversationForEveryone={deleteConversationForEveryone}
        getConversationDisplayName={getConversationDisplayName}
        notificationPermission={notificationPermission}
        onOpenConversationNotes={openConversationNotes}
        loadingOlderMessages={loadingOlderMessages}
        onLoadOlderMessages={() => loadOlderMessages()}
        onLoadOlderMessagesForJump={loadOlderMessages}
        getParticipantDisplayName={getParticipantDisplayName}
        showFilesPanel={showFilesPanel}
        setShowFilesPanel={setShowFilesPanel}
        conversationFiles={conversationFiles}
        onToggleAiAssistant={onToggleAiAssistant}
        aiHistoryStartSaving={aiHistoryStartSaving}
        onUpdateAiHistoryStartAtUtc={handleUpdateAiHistoryStartAtUtc}
        isSoundEnabled={isSoundEnabled}
        onToggleSound={onToggleSound}
        filesSearchTerm={filesSearchTerm}
        setFilesSearchTerm={setFilesSearchTerm}
        groupedConversationFiles={groupedConversationFiles}
        filesPanelRef={filesPanelRef}
        messagesPaneRef={messagesPaneRef}
        meSender={meSender}
        getMessageTypeLabel={getMessageTypeLabel}
        showReactionPicker={showReactionPicker}
        pendingReactionKeys={pendingReactionKeys}
        setShowReactionPicker={setShowReactionPicker}
        onReaction={onReaction}
        onModerateInstagramComment={onModerateInstagramComment}
        onModerateMessengerComment={onModerateMessengerComment}
        onDeleteMessage={onDeleteMessage}
        translatingMessageId={translatingMessageId}
        translatedMessagesById={translatedMessagesById}
        translatedMessageVisibilityById={translatedMessageVisibilityById}
        onTranslateMessage={handleTranslateMessage}
        onToggleTranslatedMessageVisibility={handleToggleTranslatedMessageVisibility}
        renderMessageContent={renderMessageContent}
        mentionDetailsPopup={mentionDetailsPopup}
        imageViewerPopup={imageViewerPopup}
        unreadCount={unreadCount}
        scrollToBottom={scrollToBottom}
        aiDraft={aiDraft}
        aiDraftError={aiDraftError}
        aiSummary={aiSummary}
        aiSummaryError={aiSummaryError}
        aiMemory={aiMemory}
        aiMemoryError={aiMemoryError}
        aiMemoryLoading={aiMemoryLoading}
        refreshingAiMemory={refreshingAiMemory}
        clearingAiMemory={clearingAiMemory}
        copyingAiMemory={copyingAiMemory}
        aiThinkingSteps={aiThinkingSteps}
        savingAiSummaryAsNote={notesBusy}
        copyingAiSummary={copyingAiSummary}
        onUseAiDraft={onUseAiDraft}
        onUseAiSummary={onUseAiSummary}
        onDismissAiDraft={onDismissAiDraft}
        onDismissAiSummary={onDismissAiSummary}
        onSaveAiSummaryAsNote={handleSaveAiSummaryAsNote}
        onCopyAiSummary={handleCopyAiSummary}
        onCopyAiMemory={handleCopyAiMemory}
        onRefreshAiMemory={handleRefreshAiMemory}
        onClearAiMemory={handleClearAiMemory}
        quotedMessage={quotedMessage}
        onQuoteMessage={onQuoteMessage}
        onSelectInstagramCommentTarget={onSelectInstagramCommentTarget}
        onClearInstagramCommentTarget={onClearInstagramCommentTarget}
        onSelectMessengerCommentTarget={onSelectMessengerCommentTarget}
        onClearMessengerCommentTarget={onClearMessengerCommentTarget}
        onClearQuotedMessage={onClearQuotedMessage}
        dragActive={dragActive}
        onComposerDragOver={onComposerDragOver}
        onComposerDragLeave={onComposerDragLeave}
        onComposerDrop={onComposerDrop}
        composerFileInputRef={composerFileInputRef}
        onPickAttachments={onPickAttachments}
        isUploaderExpanded={isUploaderExpanded}
        setIsUploaderExpanded={setIsUploaderExpanded}
        isRecordingAudio={isRecordingAudio}
        onAudioRecordToggle={onAudioRecordToggle}
        isShortcutMenuOpen={isShortcutMenuOpen}
        setIsShortcutMenuOpen={setIsShortcutMenuOpen}
        onShortcutSelectCommand={onShortcutSelectCommand}
        insertComposerEmoji={insertComposerEmoji}
        onGenerateAiDraft={onGenerateAiDraftWithThinking}
        aiDraftLoading={aiDraftLoading}
        onGenerateAiSummary={onGenerateAiSummaryWithThinking}
        aiSummaryLoading={aiSummaryLoading}
        scheduledAt={scheduledAt}
        setScheduledAt={setScheduledAt}
        scheduling={scheduling}
        scheduleError={scheduleError}
        scheduledMessages={scheduledMessages}
        onSchedule={onSchedule}
        onScheduleAt={onScheduleAt}
        onCancelScheduledMessage={onCancelScheduledMessage}
        onUpdateScheduledMessageTime={onUpdateScheduledMessageTime}
        attachments={attachments}
        composer={composer}
        mentionSuggestions={mentionSuggestions}
        mentionMenuOpen={mentionMenuOpen}
        mentionActiveIndex={mentionActiveIndex}
        commandSuggestions={commandSuggestions}
        commandMenuOpen={commandMenuOpen}
        commandActiveIndex={commandActiveIndex}
        interactiveComposerMode={interactiveComposerMode}
        setInteractiveComposerMode={setInteractiveComposerMode}
        interactiveListDraft={interactiveListDraft}
        setInteractiveListDraft={setInteractiveListDraft}
        interactiveButtonsDraft={interactiveButtonsDraft}
        setInteractiveButtonsDraft={setInteractiveButtonsDraft}
        isInteractiveComposerOpen={isInteractiveComposerOpen}
        setIsInteractiveComposerOpen={setIsInteractiveComposerOpen}
        hasInteractiveListDraft={hasInteractiveListDraft}
        hasInteractiveButtonsDraft={hasInteractiveButtonsDraft}
        hasInteractiveDraft={hasInteractiveDraft}
        removeAttachment={removeAttachment}
        sendError={sendError}
        sendingMessage={sendingMessage}
        sendingProgressLabel={sendingProgressLabel}
        composerInputRef={composerInputRef}
        onComposerChange={onComposerChange}
        onComposerSelectionChange={onComposerSelectionChange}
        onComposerFocus={onComposerFocus}
        onComposerKeyDown={onComposerKeyDown}
        showWhatsAppLinkPreviewToggle={showWhatsAppLinkPreviewToggle}
        instagramComposerContext={instagramComposerContext}
        instagramReplyMode={instagramReplyMode}
        setInstagramReplyMode={setInstagramReplyMode}
        instagramCommentTargetMessageId={instagramCommentTargetMessageId}
        setInstagramCommentTargetMessageId={setInstagramCommentTargetMessageId}
        messengerComposerContext={messengerComposerContext}
        messengerReplyMode={messengerReplyMode}
        setMessengerReplyMode={setMessengerReplyMode}
        messengerCommentTargetMessageId={messengerCommentTargetMessageId}
        setMessengerCommentTargetMessageId={setMessengerCommentTargetMessageId}
        whatsAppLinkPreviewEnabled={whatsAppLinkPreviewEnabled}
        setWhatsAppLinkPreviewEnabled={setWhatsAppLinkPreviewEnabled}
        onSelectMention={onSelectMention}
        onSelectCommand={onSelectCommand}
        onSend={onSend}
        employeeOptions={employeeOptions}
        onAssignConversation={onAssignConversation}
        onUnassignConversation={onUnassignConversation}
        maskChannelValues={maskChannelValues}
      />
      {notesModalConversation && (
        <ConversationNotesModal
          conversation={notesModalConversation}
          currentEmployeeId={currentEmployeeId}
          isOpen={isNotesModalOpen}
          isLoading={notesLoading}
          isSaving={notesBusy}
          error={notesError}
          onClose={closeConversationNotes}
          onCreateNote={handleCreateConversationNote}
          onUpdateNote={handleUpdateConversationNote}
          onDeleteNote={handleDeleteConversationNote}
        />
      )}
    </div>
    </div>
  );
};

export { ConversationsPage };
export default ConversationsPage;
